import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, User, SignOut, List, X, TelegramLogo } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function Header() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const [brand, setBrand] = useState("DutchMoney Shop");
  const [tg, setTg] = useState("DutchMoney420");
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => {
    api.get("/meta/config").then(({ data }) => { setBrand(data.brand); setTg(data.telegram_handle); }).catch(()=>{});
  }, []);

  const links = [
    { to: "/", label: "Shop" },
    { to: "/delivery", label: "Delivery" },
    { to: "/shop?category=handmade", label: "Handmade" },
    { to: "/shop?category=digital", label: "Digital" },
    { to: "/become-vendor", label: "Sell" },
    { to: "/newsletter", label: "Newsletter" },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/80 border-b border-white/10" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
          <div className="w-8 h-8 bg-signal flex items-center shadow-[0_0_14px_rgba(255,7,58,0.7)] justify-center">
            <span className="font-display font-black text-white text-sm">D</span>
          </div>
          <span className="font-display font-black tracking-tighter text-lg hidden sm:inline">{brand}</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link key={l.to} to={l.to}
              className="overline hover:text-signal transition-colors"
              data-testid={`nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a href={`https://t.me/${tg}`} target="_blank" rel="noreferrer"
             className="hidden sm:flex items-center gap-2 text-telegram hover:bg-telegram hover:text-white border border-telegram px-3 py-2 text-xs tracking-widest uppercase font-mono transition-colors"
             data-testid="telegram-support-link">
            <TelegramLogo size={16} weight="fill" /> Support
          </a>
          <Link to="/cart" className="relative p-2 hover:text-signal transition-colors" data-testid="cart-icon">
            <ShoppingBag size={22} weight="duotone" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-signal text-white text-xs w-5 h-5 flex items-center justify-center font-mono"
                    data-testid="cart-count">{count}</span>
            )}
          </Link>
          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              {(user.role === "admin" || user.role === "vendor") && (
                <Link to="/admin" className="overline text-signal" data-testid="admin-link">
                  {user.role === "admin" ? "Admin" : "Dashboard"}
                </Link>
              )}
              <Link to="/orders" className="overline" data-testid="orders-link">Orders</Link>
              <button onClick={async () => { await logout(); nav("/"); }}
                className="p-2 hover:text-signal transition-colors" data-testid="logout-btn" title="Sign out">
                <SignOut size={20} />
              </button>
            </div>
          ) : (
            <Link to="/login" className="hidden sm:inline overline hover:text-signal" data-testid="login-link">
              Sign in
            </Link>
          )}
          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-white/10 bg-[#111111] px-6 py-6 space-y-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.to} to={l.to} className="block overline" data-testid={`mobile-nav-${l.label.toLowerCase()}`}>
              {l.label}
            </Link>
          ))}
          {user ? (
            <>
              {(user.role === "admin" || user.role === "vendor") && <Link to="/admin" className="block overline text-signal">Dashboard</Link>}
              <Link to="/orders" className="block overline">Orders</Link>
              <button onClick={async () => { await logout(); nav("/"); }} className="block overline">Sign out</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block overline">Sign in</Link>
              <Link to="/register" className="block overline">Create account</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
